import { Component, Input, Output, EventEmitter } from '@angular/core';

@Component({
  selector: 'app-circular-progress',
  standalone: false,
  templateUrl: './circular-progress.component.html',
  styleUrls: ['./circular-progress.component.scss']
})
export class CircularProgressComponent {
  @Input() progress: number = 0;
  @Input() size: number = 120;
  @Input() strokeWidth: number = 6;
  @Input() showPercentage: boolean = true;
  @Input() clickable: boolean = false;
  @Input() customColors: {
    low?: string;
    medium?: string;
    high?: string;
    complete?: string;
  } = {};
  @Input() imageSrc?: string;
  @Input() imageAlt: string = 'Profile Image';
  @Input() showImage: boolean = true;
  @Input() showBackground: boolean = true;
  @Input() animationDuration: number = 0.8;
  @Input() enableHover: boolean = true;
  @Input() enablePulse: boolean = true;
  @Input() enableBounce: boolean = true;
  @Input() enableScale: boolean = true;
  @Input() useDefaultImage: boolean = false;
  @Input() defaultImageName: string = '';
  @Input() defaultImageBgColor: string = '#ff6b6b';
  @Input() defaultImageColor: string = '#ffffff';
  @Input() defaultImageFontSize: string = '16px';
  @Input() defaultImageLetterSpacing: string = '1px';

  @Output() progressClick = new EventEmitter<void>();

  Math = Math;

  get radius(): number {
    return (this.size / 2) - (this.strokeWidth / 2);
  }

  get circumference(): number {
    return 2 * Math.PI * this.radius;
  }

  get strokeDasharray(): string {
    return this.circumference.toString();
  }

  get strokeDashoffset(): string {
    const offset = this.circumference * (1 - this.progress / 100);
    return offset.toString();
  }

  getProgressColor(): string {
    const defaultColors = {
      low: '#FF6B6B',      // Red
      medium: '#FFD93D',   // Yellow
      high: '#75C2F6',     // Blue
      complete: '#6FE6FC'  // Teal
    };

    const colors = { ...defaultColors, ...this.customColors };

    if (this.progress < 30) {
      return colors.low;
    } else if (this.progress < 70) {
      return colors.medium;
    } else if (this.progress < 100) {
      return colors.high;
    } else {
      return colors.complete;
    }
  }

  getImageSrc(): string {
    return this.imageSrc || '../../../assets/icon/icone-profile/profile_img.svg';
  }

  onProgressClick(): void {
    if (this.clickable) {
      this.progressClick.emit();
    }
  }

  getAnimationStyles(): any {
    const styles: any = {};
    
    if (this.animationDuration > 0) {
      styles.transition = `stroke-dashoffset ${this.animationDuration}s cubic-bezier(0.4, 0, 0.2, 1)`;
    }
    
    return styles;
  }

  shouldShowDefaultImage(): boolean {
    return this.useDefaultImage && (!this.imageSrc || this.imageSrc.length === 0);
  }

  getDefaultImageInitials(): string {
    if (!this.defaultImageName) return '';
    
    const names = this.defaultImageName.trim().split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    } else if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return '';
  }

  getDefaultImageStyles(): any {
    return {
      backgroundColor: this.defaultImageBgColor,
      color: this.defaultImageColor,
      fontSize: this.defaultImageFontSize,
      letterSpacing: this.defaultImageLetterSpacing
    };
  }
}
